import type { Palette, PrimitiveColor, SemanticColor } from '..';
import {
  blueColorToken,
  grayColorToken,
  greenColorToken,
  OrangeColorToken,
  redColorToken,
  systemColorToken,
  YellowColorToken,
} from './token';

const primaryColorToken: PrimitiveColor['primary'] = {
  50: '#E8F9FC',
  100: '#B7EEF6',
  200: '#94E5F1',
  300: '#64D9EB',
  400: '#45D2E7',
  500: '#17C7E1',
  600: '#15B5CD',
  700: '#108DA0',
  800: '#0D6D7C',
  900: '#0A545F',
};

const secondaryColorToken: PrimitiveColor['primary'] = {
  50: '#E8FAF8',
  100: '#B8F0E8',
  200: '#95E9DD',
  300: '#65DFCD',
  400: '#47D9C4',
  500: '#19CFB5',
  600: '#17BCA5',
  700: '#129381',
  800: '#0E7264',
  900: '#0B574C',
};

export const semanticTextColor: SemanticColor['text'] = {
  primary: 'gray.900',
  assistance: 'gray.400',
  primaryInverse: 'system.white',
  secondary: 'gray.600',
  tertiary: 'gray.500',
  disabled: 'gray.300',
  info: 'blue.500',
  success: 'green.500',
  error: 'red.500',
  danger: 'orange.500',
  interactive: {
    primary: 'primary.500',
    primaryHover: 'primary.600',
    primaryPressed: 'primary.700',
    secondary: 'gray.600',
    secondaryHover: 'gray.700',
    secondaryPressed: 'gray.800',
    selected: 'primary.500',
    inverse: 'system.white',
    danger: 'orange.500',
    dangerHover: 'orange.600',
    dangerPressed: 'orange.700',
    error: 'red.500',
    errorHover: 'red.600',
    errorPressed: 'red.700',
    info: 'blue.500',
    infoHover: 'blue.600',
    infoPressed: 'blue.700',
  },
};

export const semanticBgColor: SemanticColor['bg'] = {
  primary: 'system.white',
  secondary: 'gray.50',
  tertiary: 'gray.100',
  disabledLight: 'gray.50',
  disabledDark: 'gray.300',
  primaryInverse: 'system.white',
  info: 'blue.500',
  infoSubtle: 'blue.50',
  success: 'green.500',
  successSubtle: 'green.50',
  error: 'red.500',
  errorSubtle: 'red.50',
  danger: 'orange.500',
  dangerSubtle: 'orange.50',
  interactive: {
    primary: 'primary.500',
    primaryHover: 'primary.600',
    primaryPressed: 'primary.700',
    secondary: 'gray.100',
    secondaryHover: 'gray.150',
    secondaryPressed: 'gray.200',
    selected: 'primary.50',
    selectedHover: 'primary.50',
    selectedPressed: 'primary.100',
    destructive: 'red.500',
    destructiveHover: 'red.600',
    destructivePressed: 'red.700',
    danger: 'orange.500',
    dangerHover: 'orange.600',
    dangerPressed: 'orange.700',
    error: 'red.500',
    errorHover: 'red.600',
    errorPressed: 'red.700',
    info: 'blue.500',
    infoHover: 'blue.600',
    infoPressed: 'blue.700',
  },
  dimmer: {
    '0': 'rgba(0,0,0,0)',
    '20': 'rgba(0,0,0,0.2)',
    '40': 'rgba(0,0,0,0.4)',
    '60': 'rgba(0,0,0,0.6)',
    '80': 'rgba(0,0,0,0.8)',
    '100': 'rgba(0,0,0,1)',
  },
};

export const semanticBorderColor: SemanticColor['border'] = {
  primary: 'gray.300',
  secondary: 'gray.200',
  tertiary: 'gray.150',
  disabled: 'gray.300',
  focusRing: 'primary.600',
  info: 'blue.500',
  infoSubtle: 'blue.200',
  success: 'green.500',
  successSubtle: 'green.200',
  error: 'red.500',
  errorSubtle: 'red.200',
  danger: 'orange.500',
  dangerSubtle: 'orange.200',
  interactive: {
    primary: 'primary.500',
    primaryHover: 'primary.600',
    primaryPressed: 'primary.700',
    secondary: 'gray.200',
    secondaryHover: 'gray.200',
    secondaryPressed: 'gray.400',
    selected: 'primary.500',
    inverse: 'system.white',
    danger: 'orange.500',
    dangerHover: 'orange.600',
    dangerPressed: 'orange.700',
    error: 'red.500',
    errorHover: 'red.600',
    errorPressed: 'red.700',
    info: 'blue.500',
    infoHover: 'blue.600',
    infoPressed: 'blue.700',
  },
};

export const semanticIconColor: SemanticColor['icon'] = {
  primary: 'gray.900',
  secondary: 'gray.600',
  tertiary: 'gray.500',
  brand: 'primary.500',
  disabled: 'gray.300',
  info: 'blue.500',
  success: 'green.500',
  danger: 'orange.500',
  error: 'red.500',
  interactive: {
    primary: 'primary.500',
    primaryHover: 'primary.600',
    primaryPressed: 'primary.700',
    secondary: 'gray.600',
    secondaryHover: 'gray.700',
    secondaryPressed: 'gray.800',
    selected: 'primary.500',
    inverse: 'system.white',
    danger: 'orange.500',
    dangerHover: 'orange.600',
    dangerPressed: 'orange.700',
    error: 'red.500',
    errorHover: 'red.600',
    errorPressed: 'red.700',
    info: 'blue.500',
    infoHover: 'blue.600',
    infoPressed: 'blue.700',
  },
};

export const palette: Palette = {
  gray: grayColorToken,
  blue: blueColorToken,
  green: greenColorToken,
  orange: OrangeColorToken,
  yellow: YellowColorToken,
  red: redColorToken,
  system: systemColorToken,
  primary: primaryColorToken,
  secondary: secondaryColorToken,
  text: semanticTextColor,
  bg: semanticBgColor,
  icon: semanticIconColor,
  border: semanticBorderColor,
};
